import { Component, EventEmitter, Input, Output, forwardRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { HisControlValueAccessor } from '../../core/forms/his-control-value-accessor';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { TreeNodeData } from '../model/tree-node-data';

export const HISTREESELECTCOMPONENT_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef( () => HisTreeSelectComponent ),
    multi: true
};

@Component( {
    providers: [HISTREESELECTCOMPONENT_VALUE_ACCESSOR],
    selector: 'his-tree-select',
    templateUrl: './his-tree-select.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisTreeSelectComponent extends HisControlValueAccessor {
    notificationService = inject(HisNotificationService);
    uiService = inject(HisUiService);


    // unique id for html
    @Input() uid: string;

    // title of the popup window
    @Input() title: string;

    @Input() treename: string;

    // select only a single element?
    @Input() singleSelect = false;

    // The select tree for the popup sub-component.
    // The TreeTable component requires an _array_ of TreeNode objects as its value.
    tree: TreeNode[];

    // display column with subtree select checkboxes in the popup?
    @Input() showSubtreeSelectColumn = true;

    // display autocomplete ?
    @Input() showAutocomplete = true;
    // minimum length of autocomplete input to trigger a search
    @Input() minLength = 1;
    // maximum number of suggestions to show in autocomplete (null -> show all)
    @Input() limit: number = null;
    // show icon to open dropdown?
    @Input() showDropdownIcon = true;
    // show null item in autocomplete select list?
    @Input() showNullItem = false;
	
	@Input() noSelectableNodeIds: number[] = [];
	
    @Input() noSelectableSubtreeNodeIds: number[] = [];

    // all elements of the tree as a flat list, required by autocomplete
    selectList: TreeNode[];

    // all elements of the tree which are not already selected for autocomplete
    selectableList: TreeNode[] = [];

    // The component model: An array of the data sections of the selected tree elements.
    // The Input() is only required to make the reset button in the generic search work!
    // Without injecting widget.value in his-search-form-widget.component.html, we'ld have distinct view/model objects.
    @Input() selectedData: TreeNodeData[] = [];

    /** Limit the number of selections displayed. Overflow will be combined to a single label. */
    // default to 10, similar to JSF
    @Input() maxDisplayedSelections = 10;
    protected showAllSelected = false;

    // event emitted when selectedData changes
    @Output() changed = new EventEmitter<TreeNodeData[]>();

    @Input( 'tree' )
    set treeValues( tree: TreeNode[] ) {
        if ( tree ) {
            this.tree = tree;
            // convert tree (possibly having several root nodes) into a flat list
            console.log( 'Convert tree to list: tree =', this.tree );
            this.selectList = [];
            let root: TreeNode;
            for ( root of this.tree ) {
                let subList = this.subtreeToList( root );
                this.selectList = this.selectList.concat( subList );
            }
            this.selectableList = [...this.selectList];
        }
    }

    private subtreeToList( node: TreeNode ): TreeNode[] {
        let list: TreeNode[] = [node];
        let child: TreeNode;
        for ( child of node.children ) {
            let subList: TreeNode[] = this.subtreeToList( child );
            list = list.concat( subList );
        }
        return list;
    }

    getHtmlFocusElementId(): string {
        return this.uid;
    }

    /**
     * Add a new element selected by autocomplete.
     *
     * @param selectedElement the element to add to the selection
     */
    addSelectedElement( selectedElement: TreeNode ) {
        console.log( 'Element selected by autocomplete:', selectedElement );
        // In multiSelect mode we do not want to add an element twice; just tell the user that the element is already there.
        // In singleSelect mode we do not need that check because the data is substituted anyway.
        if ( !this.singleSelect && this.isElementAlreadySelected( selectedElement.data.id ) ) {
            let message = this.uiService.translate( 'genericSearch.alreadySelected', selectedElement.data.defaulttext );
            this.notificationService.clear();
            this.notificationService.info( message, 'Info' );
            return;
        }

        // Add selectedElement to selectedData: In singleSelect mode selectedData is replaced by the new selection;
        // in multiSelect mode the selection is added to selectedData. The latter requires the spread-operator
        // to have model and view synchronized (with push() the reset button wouldn't work correctly)
        let data: any = selectedElement.data;
        data.selected = true;
        this.selectedData = this.singleSelect ? [data] : [...this.selectedData, data];
        // remove selected element from selectableList
        this.selectableList = this.selectableList.filter(it => it !== selectedElement);
        this.changed.emit( this.selectedData );
        this.onModelChange( this );
    }

    private isElementAlreadySelected( id: number ): boolean {
        for ( let option of this.selectedData ) {
            if ( option.id === id ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Vom Auswahl-Popup ausgelöstes Event.
     *
     * @param selectedData Array der neu ausgewählten Baum-Elemente
     */
    selectedDataChange( selectedData: TreeNodeData[] ) {
        this.selectedData = selectedData;
        // update selectables
        this.selectableList = this.selectList.filter(it => this.selectedData.find(el => it.data.id === el.id) === undefined);
        console.log( 'Tree nodes selected in popup:', this.selectedData );
        this.changed.emit( this.selectedData );
        this.onModelChange( this );

        // re-hide any future overflow after the button disappears
        if (this.selectedData.length <= this.maxDisplayedSelections) {
            this.showAllSelected = false;
        }
    }

    /**
     * Wird durch Klick auf das Mülleimer-Icon vor einem ausgewähltem Element aufgerufen
     * und löscht das übergebene Element aus der Auswahl.
     *
     * @param obj zu löschendes Element
     */
    deleteObj( obj: any ) {
        this.selectedData.splice( this.selectedData.indexOf( obj ), 1 );
        console.log( 'Deleted selected element', obj, '-> new selection is', this.selectedData );
        // it becomes selectable
        let item = this.selectList.find(it => it.data == obj);
        this.selectableList.push(item);
        console.log('Selectable: ', this.selectableList);
        this.changed.emit( this.selectedData );
    }

    toggleShowAllSelected() {
        this.showAllSelected = !this.showAllSelected;
    }
}
